import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import { TrendingUp, Clock, Download } from 'lucide-react';
import PageTitle from '../../../layouts/PageTitle';
import * as api from '../../../../services/api/projectsApi';
import * as S from '../../Common/internalToolsStyles';

const TABS = [
  { key: 'profitability', label: 'Lönsamhet', icon: TrendingUp },
  { key: 'wip', label: 'Ofakturerat', icon: Clock },
];

const fmtNum = (n) => n != null ? Math.round(n * 100) / 100 : '—';
const fmtSEK = (n) => n != null ? Math.round(n).toLocaleString('sv-SE') + ' kr' : '—';
const fmtPct = (n) => n != null ? Math.round(n * 10) / 10 + '%' : '—';

const ProjectReportsPage = () => {
  const { companyId } = useParams();
  const [activeTab, setActiveTab] = useState('profitability');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const now = new Date();
  const [startDate, setStartDate] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
  const [endDate, setEndDate] = useState(now.toISOString().slice(0, 10));

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let result;
      switch (activeTab) {
        case 'profitability':
          result = await api.getProjectProfitabilityReport(companyId, { startDate, endDate });
          setData(result.data.data);
          break;
        case 'wip':
          result = await api.getProjectWipAgingReport(companyId);
          setData(result.data.data);
          break;
        default:
          setData(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda rapport');
    } finally {
      setLoading(false);
    }
  }, [companyId, activeTab, startDate, endDate]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleExport = async () => {
    try {
      const type = activeTab === 'wip' ? 'wip-aging' : activeTab;
      const res = await api.exportProjectReportCsv(companyId, { type, startDate, endDate });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `projekt-rapport-${activeTab}-${startDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Kunde inte exportera rapport');
    }
  };

  const renderProfitability = () => {
    if (!data || !Array.isArray(data)) return null;
    return (
      <table style={S.table}>
        <thead>
          <tr style={S.tableHeaderRow}>
            <th style={S.tableHeaderCell}>Projekt</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Timmar</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Intäkt</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Kostnad</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Marginal</th>
            <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Budget</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => {
            const margin = row.revenue - row.cost;
            const marginPct = row.revenue > 0 ? (margin / row.revenue) * 100 : 0;
            const budgetPct = row.budget_hours ? (row.total_hours / row.budget_hours) * 100 : null;
            return (
              <tr key={i} style={S.tableRow}>
                <td style={S.tableCell}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {row.project_color && <span style={S.colorDot(row.project_color, 8)} />}
                    <span style={S.text.bodySmallMedium}>{row.project_name}</span>
                  </span>
                </td>
                <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtNum(row.total_hours)}h</td>
                <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtSEK(row.revenue)}</td>
                <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtSEK(row.cost)}</td>
                <td style={{ ...S.tableCell, textAlign: 'right' }}>
                  <span style={{ color: marginPct >= 30 ? S.colors.success : marginPct >= 10 ? S.colors.warning : S.colors.danger }}>
                    {fmtSEK(margin)} ({fmtPct(marginPct)})
                  </span>
                </td>
                <td style={{ ...S.tableCell, textAlign: 'right' }}>{budgetPct != null ? fmtPct(budgetPct) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderWip = () => {
    if (!data || !Array.isArray(data)) return null;
    const bucketLabels = { current: 'Aktuell', '30d': '30 dagar', '60d': '60 dagar', '90d_plus': '90+ dagar' };
    const bucketColors = {
      current: '#059669',
      '30d': '#f59e0b',
      '60d': '#dc2626',
      '90d_plus': '#7c3aed',
    };
    return (
      <>
        <table style={S.table}>
          <thead>
            <tr style={S.tableHeaderRow}>
              <th style={S.tableHeaderCell}>Kund</th>
              <th style={S.tableHeaderCell}>Projekt</th>
              <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Timmar</th>
              <th style={{ ...S.tableHeaderCell, textAlign: 'right' }}>Värde</th>
              <th style={{ ...S.tableHeaderCell, textAlign: 'center' }}>Ålder</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i} style={S.tableRow}>
                <td style={S.tableCell}>{row.customer_name}</td>
                <td style={S.tableCell}><span style={S.text.bodySmallMedium}>{row.project_name}</span></td>
                <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtNum(row.hours)}h</td>
                <td style={{ ...S.tableCell, textAlign: 'right', ...S.text.number }}>{fmtSEK(row.value)}</td>
                <td style={{ ...S.tableCell, textAlign: 'center' }}>
                  <span style={S.badge(bucketColors[row.age_bucket] || S.colors.textMuted, 'transparent')}>
                    {bucketLabels[row.age_bucket] || row.age_bucket}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  };

  const renderTab = () => {
    if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spinner animation="border" size="sm" /></div>;
    if (!data) return (
      <div style={S.emptyState}>
        <div style={S.emptyStateIcon}><TrendingUp size={48} /></div>
        <h4 style={S.emptyStateTitle}>Ingen data ännu</h4>
        <p style={S.emptyStateDesc}>
          Justera datumintervallet eller godkänn fler tidrapporter för att se rapporter.
        </p>
      </div>
    );
    switch (activeTab) {
      case 'profitability': return renderProfitability();
      case 'wip': return renderWip();
      default: return null;
    }
  };

  // Thin stats strip — a one-liner derived from the active tab's data.
  // Mirrors the Hantera pages' strip pattern; replaces the bigger
  // "totalt ofakturerat" headline that used to live inside the WIP tab.
  const stripSummary = (() => {
    if (!data || !Array.isArray(data)) return null;
    if (activeTab === 'profitability') {
      const totals = data.reduce((acc, r) => {
        acc.hours += Number(r.total_hours) || 0;
        acc.revenue += Number(r.revenue) || 0;
        acc.cost += Number(r.cost) || 0;
        return acc;
      }, { hours: 0, revenue: 0, cost: 0 });
      const margin = totals.revenue - totals.cost;
      const marginPct = totals.revenue > 0 ? (margin / totals.revenue) * 100 : 0;
      return [
        { label: 'Projekt', value: data.length },
        { label: 'Timmar', value: `${fmtNum(totals.hours)}h` },
        { label: 'Intäkt', value: fmtSEK(totals.revenue) },
        { label: 'Kostnad', value: fmtSEK(totals.cost) },
        { label: 'Marginal', value: `${fmtSEK(margin)} (${fmtPct(marginPct)})`, color: marginPct >= 30 ? S.colors.success : marginPct >= 10 ? S.colors.warning : S.colors.danger },
      ];
    }
    if (activeTab === 'wip') {
      const total = data.reduce((s, r) => s + (Number(r.value) || 0), 0);
      const totalHours = data.reduce((s, r) => s + (Number(r.hours) || 0), 0);
      return [
        { label: 'Ofakturerat', value: fmtSEK(total) },
        { label: 'Timmar', value: `${fmtNum(totalHours)}h` },
        { label: 'Projekt', value: data.length },
      ];
    }
    return null;
  })();

  return (
    <>
      <PageTitle activeMenu="Rapporter" motherMenu="Projekt" />

      {error && <Alert variant="warning" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* Thin stats strip — active-tab totals at a glance. */}
      {stripSummary && stripSummary.length > 0 && (
        <div className="proj-stats-strip">
          {stripSummary.map((s, i) => (
            <React.Fragment key={s.label}>
              {i > 0 && <span className="proj-stat-sep">·</span>}
              <span className="proj-stat">
                <span className="proj-stat-label">{s.label}:</span>
                <b style={{ color: s.color || '#0f172a' }}>{s.value}</b>
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Toolbar — tab pills + date range + CSV export. */}
      <div className="proj-toolbar">
        <div className="proj-pills" style={{ marginBottom: 0 }}>
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                className={`proj-pill ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="proj-toolbar-right">
          {activeTab !== 'wip' && (
            <>
              <input
                type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="proj-date-input"
              />
              <span style={{ color: '#94a3b8', fontSize: 13 }}>–</span>
              <input
                type="date" value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="proj-date-input"
              />
            </>
          )}
          <button onClick={handleExport} className="proj-csv-btn" title="Exportera CSV">
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      <div style={{ ...S.card, padding: 0 }}>
        <div style={{ padding: 0 }}>
          {renderTab()}
        </div>
      </div>

      <style>{`
        .proj-stats-strip {
          display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
          padding: 8px 14px; margin-bottom: 12px;
          background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 8px;
          font-size: 0.8125rem; color: #475569;
        }
        .proj-stat-label { margin-right: 4px; color: #64748b; }
        .proj-stat b { font-variant-numeric: tabular-nums; }
        .proj-stat-sep { color: #cbd5e1; }

        .proj-toolbar {
          display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
          padding: 10px 14px; background: white;
          border: 1px solid #e5e7eb; border-radius: 10px;
          flex-wrap: wrap;
        }
        .proj-toolbar-right { display: flex; align-items: center; gap: 8px; margin-left: auto; }
        .proj-date-input {
          height: 34px; padding: 0 10px;
          border: 1px solid #e5e7eb; border-radius: 8px;
          font-size: 0.8125rem; background: #f9fafb;
          transition: all 0.15s;
        }
        .proj-date-input:focus {
          outline: none; background: white;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
        }
        .proj-csv-btn {
          height: 34px; padding: 0 12px;
          border: 1px solid #e5e7eb; border-radius: 8px;
          background: white; color: #475569;
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 0.8125rem; font-weight: 500; cursor: pointer;
          transition: all 0.15s;
        }
        .proj-csv-btn:hover { background: #f3f4f6; color: #0f172a; }

        .proj-pills { display: flex; flex-wrap: wrap; gap: 6px; }
        .proj-pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 12px; border: 1px solid #d1d5db; background: white;
          border-radius: 999px; font-size: 0.8125rem; font-weight: 500;
          color: #111827; cursor: pointer; transition: all 0.15s;
        }
        .proj-pill:hover { background: #f3f4f6; }
        .proj-pill.active {
          background: var(--rgba-primary-2); color: var(--bs-primary);
          border-color: var(--bs-primary); font-weight: 600;
        }
      `}</style>
    </>
  );
};

export default ProjectReportsPage;
